// src/components/BusquedaCamaraPaciente.js
// Dependencia: npm install html5-qrcode
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../api/api';

const SCANNER_ID = 'qr-scanner-container';

const BusquedaCamaraPaciente = ({ open, handleClose, navigate }) => {
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [scanning, setScanning]         = useState(false);
  const [lecturaExitosa, setLecturaExitosa] = useState('');

  const scannerRef      = useRef(null);   // instancia Html5Qrcode
  const isProcessingRef = useRef(false);

  // ── Detener el scanner de forma segura ──────────────────────────
  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        // Estado 2 = SCANNING, estado 3 = PAUSED
        if (state === 2 || state === 3) {
          await scannerRef.current.stop();
        }
      } catch (_) { /* ignorar si ya estaba detenido */ }
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  // ── Iniciar el scanner ──────────────────────────────────────────
  const startScanner = useCallback(async () => {
    isProcessingRef.current = false;
    setError('');
    setLoading(false);
    setLecturaExitosa('');

    await stopScanner();

    // Esperar a que el DOM renderice el contenedor
    await new Promise(r => setTimeout(r, 300));

    const container = document.getElementById(SCANNER_ID);
    if (!container) return;

    try {
      const scanner = new Html5Qrcode(SCANNER_ID, {
        verbose: false,
        // Procesamiento mejorado para papel térmico:
        // experimentalFeatures permite mejoras de imagen internas
        experimentalFeatures: { useBarCodeDetectorIfSupported: false }
      });
      scannerRef.current = scanner;

      await scanner.start(
        // Cámara trasera preferida (trasera tiene mejor enfoque para papel)
        { facingMode: 'environment' },
        {
          fps: 10,             // 10fps — le da tiempo al autoenfoque
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
          // Estas opciones mejoran la detección en papel térmico:
          disableFlip: false,  // intenta también imagen espejada
          rememberLastUsedCamera: true,
        },
        // Éxito
        (decodedText) => {
          if (isProcessingRef.current) return;
          isProcessingRef.current = true;
          handleDecode(decodedText);
        },
        // Error de frame — ignorar silenciosamente
        (_err) => { }
      );

      setScanning(true);
    } catch (err) {
      console.error('Error al iniciar cámara:', err);
      setError('No se pudo acceder a la cámara. Verifica los permisos.');
    }
  }, [stopScanner]);

  // ── Procesar el QR decodificado ─────────────────────────────────
  const handleDecode = useCallback(async (rawText) => {
    console.log('QR leído:', rawText);
    setLecturaExitosa(rawText);
    setLoading(true);
    await stopScanner();

    try {
      let codeToSearch = rawText.trim();
      if (rawText.includes('PAC-')) {
        const match = rawText.match(/PAC-[A-Z0-9]+/i);
        if (match) codeToSearch = match[0].toUpperCase();
      }

      const res = await api.get(`/pacientes/qr/${codeToSearch}`);
      const paciente = res.data;

      if (paciente?.id_cliente) {
        navigate(`/clientes/${paciente.id_cliente}`, {
          state: { mensaje: `¡Bienvenido, ${paciente.nombre_paciente}!` }
        });
        handleClose();
      } else {
        setError(`QR válido pero sin cliente vinculado: ${rawText}`);
        setLoading(false);
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = status === 404
        ? 'Código no encontrado (404).'
        : 'Error de conexión con el servidor.';
      setError(`${msg} Leído: "${rawText}"`);
      setLoading(false);
    }
  }, [stopScanner, navigate, handleClose]);

  // ── Ciclo de vida del diálogo ───────────────────────────────────
  useEffect(() => {
    if (open) {
      startScanner();
    } else {
      stopScanner();
    }
    return () => { stopScanner(); };
  }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps

  const handleReintentar = () => startScanner();

  const handleCerrar = useCallback(async () => {
    await stopScanner();
    handleClose();
  }, [stopScanner, handleClose]);

  // ── UI ──────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={handleCerrar} maxWidth="sm" fullWidth>
      <DialogTitle>Escanear QR de Paciente</DialogTitle>

      <DialogContent>
        {error && !loading && (
          <Alert
            severity="warning"
            sx={{ mb: 2, '& .MuiAlert-message': { wordBreak: 'break-all' } }}
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>QR leído correctamente.</Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mt: 1, wordBreak: 'break-all' }}
            >
              "{lecturaExitosa}"
            </Typography>
            <Typography sx={{ mt: 1 }}>Buscando en el sistema...</Typography>
          </Box>
        ) : (
          <Box>
            {/* Contenedor del scanner — html5-qrcode inyecta el video aquí */}
            <Box
              sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#000',
                // Ocultamos el contenedor cuando no escaneamos pero lo mantenemos en DOM
                display: scanning || (!scanning && !error) ? 'block' : 'none',
              }}
            >
              <div id={SCANNER_ID} style={{ width: '100%' }} />
            </Box>

            {!scanning && error && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="text.secondary">
                  Cámara detenida. Presiona "Reintentar" para escanear de nuevo.
                </Typography>
              </Box>
            )}

            {/* Tip para papel térmico */}
            {scanning && (
              <Typography
                variant="caption"
                sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}
              >
                💡 En papel térmico: mantén la etiqueta a ~15cm y evita reflejos de luz directa
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCerrar} disabled={loading}>Cerrar</Button>
        {!scanning && !loading && (
          <Button variant="contained" onClick={handleReintentar}>
            Reintentar escaneo
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BusquedaCamaraPaciente;